import React, { FC } from 'react';

import classNames from 'classnames';

import { EvSection, EvSpinner, EvTitle, LinkIcon } from '@evinced-private/ui-common';

import {
	MFA_SESSION_COPY_URL_BUTTON_DAID,
	MFA_SESSION_HEADER_DAID,
	MFA_SESSION_TITLE_DAID
} from 'data-automation-ids/MfaDashboardAutomationIds';
import { getCurrentMfaFullUrl } from 'src/apps/mfa/helpers/MfaDashboardHelpers';
import { useMfaSessionDetailsQuery } from 'src/apps/mfa/hooks/useMfaSessionDetailsQuery';
import { useSetMfaBreadcrumbs } from 'src/apps/mfa/hooks/useSetMfaBreadcrumbs';
import { CopyToClipboard } from 'src/common/components/copy-to-clipboard/CopyToClipboard';
import { ReportInfo } from 'src/common/components/report-info/ReportInfo';

import { getMfaInfoItems } from './MfaSingleSessionHeaderHelper';

import './MfaSingleSessionHeader.scss';

interface IMfaSessionHeaderProps {
	sessionId: string;
	onSessionNotFoundCallback: () => void;
}

export const MfaSingleSessionHeader: FC<IMfaSessionHeaderProps> = ({
	sessionId,
	onSessionNotFoundCallback
}) => {
	const sessionDetailsQuery = useMfaSessionDetailsQuery(sessionId);

	const { sessionName } = sessionDetailsQuery.data ?? {};

	useSetMfaBreadcrumbs(sessionId, sessionName);

	const isSessionError =
		!sessionId ||
		sessionDetailsQuery.isError ||
		(sessionDetailsQuery.data === null && !sessionDetailsQuery.isLoading);

	if (isSessionError) {
		onSessionNotFoundCallback();
		return null;
	}

	return sessionDetailsQuery.isLoading ? (
		<EvSpinner />
	) : (
		<div className="mfa-single-session-header" data-automation-id={MFA_SESSION_HEADER_DAID}>
			<div className="title-container">
				<EvTitle
					titleText={sessionName}
					headingLevel={1}
					className="main-title page-heading"
					dataAutomationId={MFA_SESSION_TITLE_DAID}
				/>
				<CopyToClipboard
					renderJustIcon
					renderTooltip
					textToCopy={getCurrentMfaFullUrl(sessionId)}
					className="copy-link-button"
					iconTitle="Copy Session URL"
					icon={LinkIcon}
					dataAutomationId={MFA_SESSION_COPY_URL_BUTTON_DAID}
				/>
			</div>
			<EvSection
				ariaLabel={`Session ${sessionName} info`}
				skipLinkId="session-info-section"
				ariaLive="polite"
				className={classNames('dashboard-section', 'mfa-session-info-section')}
			>
				<ReportInfo
					titleText="Session Info"
					reportInfo={getMfaInfoItems()}
					reportDetails={sessionDetailsQuery.data}
				/>
			</EvSection>
		</div>
	);
};
